import { useEffect, useState, useRef } from 'react';
import { LayoutManager } from './components/UI/LayoutManager';
import { MicrophoneButton } from './components/UI/MicrophoneButton';
import { AcademicProgressButton } from './components/UI/AcademicProgressButton';
import { HomeButton } from './components/UI/HomeButton';
import { PauseButton } from './components/UI/PauseButton';
import { UploadActivityButton } from './components/UI/UploadActivityButton';
import { ActivitySelectionMenu, ActivityItem } from './components/ActivitySelectionMenu';
// import { Avatar3D } from './components/Avatar/Avatar3D'; // model-viewer version (no permite morph targets)
import { Avatar3DFiber as Avatar3D } from './components/Avatar/Avatar3DFiber'; // React Three Fiber (con morph targets)
import { SelectionMenu } from './components/SelectionMenu';
import { Chat } from './components/Chat/Chat';
import { useWebSocket } from './hooks/useWebSocket';
import { useScreenOrientation } from './hooks/useScreenOrientation';
import { loadPolyfills } from './utils/polyfills';
import configService from './services/configService';
import authService from './services/authService';
import webSocketService from './services/webSocketService';
import interactionTimeService from './services/interactionTimeService';
import conversationSaverService from './services/conversationSaverService';
import browserCloseHandler from './services/browserCloseHandler';
import academicService from './services/academicService';
import AcademicMode from './components/Academic/AcademicMode';
import { SelectionConfig, UserConfiguration } from './types';
import { enableTestAcademicMode } from './utils/testAcademicMode';
import { FeedbackBanner } from './components/UI/FeedbackBanner';
import feedbackService from './services/feedbackService';
import mentorAiService from './services/mentorAi/mentorAiService';
import activityAnalysisService from './services/activityAnalysisService';
import dynamoService from './services/aws/dynamo';
import googleSheetsService from './services/googleSheetsService';
import './App.css';

/**
 * Detecta si es modo coach desde la URL (sin necesidad de variables de entorno)
 */
const isCoachFromURL = (): boolean | null => {
  if (typeof window === 'undefined') {
    // console.log('[App] 🔍 isCoachFromURL: window no disponible (SSR)');
    return null;
  }
  
  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname.toLowerCase();
  
  // console.log(`[App] 🔍 Detectando desde URL - hostname: "${hostname}", pathname: "${pathname}"`);
  
  if (hostname.includes('coach') || pathname.includes('/coach')) {
    // console.log(`[App] ✅ Detectado: COACH desde URL`);
    return true;
  }
  if (hostname.includes('assistant') || pathname.includes('/assistant')) {
    // console.log(`[App] ✅ Detectado: ASSISTANT desde URL`);
    return false;
  }
  
  // console.log(`[App] ⚠️ No se detectó modo desde URL (hostname ni pathname contienen "coach" o "assistant")`);
  return null; // No se detectó desde URL
};

const resolvePromptReference = (testingPhase: boolean): string => {
  // 1. PRIORIDAD MÁXIMA: Detectar desde URL primero (para Opción B)
  const urlMode = isCoachFromURL();
  if (urlMode !== null) {
    const suffix = testingPhase ? 'dev' : 'pro';
    const promptRef = urlMode
      ? `learnenglish-coach-${suffix}`
      : `learnenglish-teacher-assistant-${suffix}`;
    // console.log(`[App] 🎯 Detección desde URL: ${urlMode ? 'coach' : 'assistant'} → ${promptRef}`);
    return promptRef;
  }

  // 2. Si no se detectó desde URL, usar override de variables (si existe)
  const override = testingPhase
    ? import.meta.env.VITE_PROMPT_REFERENCE_DEV
    : import.meta.env.VITE_PROMPT_REFERENCE_PROD;
  if (override) {
    // console.log(`[App] ⚠️ Usando override de variable de entorno: ${override} (ignorando detección desde URL)`);
    return override;
  }

  // 3. Fallback a VITE_COACH_BUILD (variable existente)
  const coachBuild = import.meta.env.VITE_COACH_BUILD === 'true';
  const suffix = testingPhase ? 'dev' : 'pro';
  const promptRef = coachBuild
    ? `learnenglish-coach-${suffix}`
    : `learnenglish-teacher-assistant-${suffix}`;
  // console.log(`[App] 📝 Usando VITE_COACH_BUILD (${coachBuild}) → ${promptRef}`);
  return promptRef;
};

const resolveAssistantId = (testingPhase: boolean): string => {
  const override = import.meta.env.VITE_ASSISTANT_ID;
  if (override) {
    return override;
  }

  // Detectar desde URL primero
  const urlMode = isCoachFromURL();
  const isCoach = urlMode !== null ? urlMode : (import.meta.env.VITE_COACH_BUILD === 'true');
  
  if (!isCoach) {
    return '';
  }
  return testingPhase
    ? 'f6982745-1580-4aa0-a65f-d70f038c9612'
    : 'b2a8ddd8-a7bd-4d51-b11a-76cd5fe0fb58';
};

function App() {
  // #region agent log
  // console.log('[DEBUG-H1,H4] App.tsx:62 - App component rendering', {timestamp:Date.now()});
  // #endregion
  
  const [shouldConnect, setShouldConnect] = useState(false);
  const { messages, sendAudioMessage, sendTextMessage, isConnecting } = useWebSocket(shouldConnect);

  // Rastrear cambios en shouldConnect para debug
  useEffect(() => {
    if (shouldConnect) {
    }
  }, [shouldConnect]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthBlocked, setIsAuthBlocked] = useState(false);
  const [initialConfigComplete, setInitialConfigComplete] = useState(false);
  const [userConfiguration, setUserConfiguration] = useState<UserConfiguration | null>(null);
  const previousConfigurationRef = useRef<UserConfiguration | null>(null); // Guardar configuración anterior para comparar
  const orientation = useScreenOrientation();
  // El chat empieza oculto hasta que se complete la selección inicial
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [showAcademicMode, setShowAcademicMode] = useState(false);
  const [academicLevel, setAcademicLevel] = useState<{ level: string; unit?: number; lesson?: number } | null>(null);
  const [isAcademicProgressVisible, setIsAcademicProgressVisible] = useState(false);
  const [isLessonInProgress, setIsLessonInProgress] = useState(false);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [isFeedbackBannerExpanded, setIsFeedbackBannerExpanded] = useState(false);
  const [isAudioPaused, setIsAudioPaused] = useState(false);
  const [isUploadingActivity, setIsUploadingActivity] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);
  const [isActivityMenuOpen, setIsActivityMenuOpen] = useState(false);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  const [currentActivityId, setCurrentActivityId] = useState<string | null>(null);
  const [completedActivityIds, setCompletedActivityIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!initialConfigComplete) {
      setShouldConnect(false);
      webSocketService.disconnect();
      webSocketService.clearChatHistory();
      conversationSaverService.resetConversationState();
      setIsAudioPaused(false);
    }
  }, [initialConfigComplete]);

  // Mostrar banner de feedback cuando hay una conversación guardable
  // No mostrar si ya se envió feedback en esta sesión
  useEffect(() => {
    const feedbackSent = sessionStorage.getItem('feedback_sent_this_session') === 'true';
    if (initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user') && !feedbackSent) {
      setShowFeedbackBanner(true);
    } else if (feedbackSent) {
      setShowFeedbackBanner(false);
    }
  }, [initialConfigComplete, messages]);

  // Verificar si hay una lección en proceso
  const checkLessonInProgress = async () => {
    try {
      const config = configService.getConfig();
      if (!config.email || !academicLevel) {
        setIsLessonInProgress(false);
        return;
      }
      
      // Verificar si el modo académico está activo en WebSocket
      // Esto indica que se inició una lección
      const academicModeActive = (webSocketService as any).academicMode?.isActive;
      
      if (academicModeActive) {
        setIsLessonInProgress(true);
      } else {
        // También verificar si hay progreso en la lección actual sin completar
        const progress = await academicService.getUserLessonProgress(
          config.email,
          academicLevel.level,
          academicLevel.unit || 0,
          academicLevel.lesson || 1
        );
        
        if (progress && !progress.all_objectives_completed) {
          // Verificar si hay algún objetivo con progreso
          const hasProgress = Object.values(progress.objectives_status).some(
            (status: any) => status.correct_count > 0
          );
          setIsLessonInProgress(hasProgress);
        } else {
          setIsLessonInProgress(false);
        }
      }
    } catch (error) {
      // console.error('[App] Error al verificar progreso de lección:', error);
      setIsLessonInProgress(false);
    }
  };

  // Handler para cambios de visibilidad del chat
  const handleChatVisibilityChange = (isVisible: boolean) => {
    setIsChatVisible(isVisible);
    conversationSaverService.setChatVisibility(isVisible);
  };

  // El chat siempre inicia oculto y se activa con el botón del chat

  useEffect(() => {
    let initializationComplete = false;
    
    // Timeout de seguridad: si la inicialización tarda más de 5 segundos, continuar de todas formas
    const initializationTimeout = setTimeout(() => {
      if (!initializationComplete) {
        // console.warn('[App] ⚠️ Timeout en inicialización, continuando de todas formas...');
        initializationComplete = true;
        setIsInitialized(true);
      }
    }, 5000);

    // Función auxiliar para marcar como inicializado de forma segura
    const markAsInitialized = () => {
      if (!initializationComplete) {
        initializationComplete = true;
        clearTimeout(initializationTimeout);
        setIsInitialized(true);
      }
    };

    // Cargar polyfills con manejo de errores
    Promise.resolve()
      .then(() => loadPolyfills())
      .catch((error) => {
        // console.warn('[App] ⚠️ Error al cargar polyfills, continuando:', error);
      })
      .then(async () => {
      try {
        // Inicializar configuración
        // Leer testingPhase desde variable de entorno (.env)
        // VITE_TESTING_PHASE=true para desarrollo, false para producción
        // Si no está definido, usar import.meta.env.DEV como fallback
        const envTestingPhase = import.meta.env.VITE_TESTING_PHASE;
        const testingPhase = envTestingPhase !== undefined
          ? (envTestingPhase === 'true' || envTestingPhase === true)
          : (import.meta.env.DEV === true); // Fallback: usar DEV de Vite

        // Detectar modo desde URL o usar VITE_COACH_BUILD como fallback
        const urlMode = isCoachFromURL();
        const coachBuild = urlMode !== null ? urlMode : (import.meta.env.VITE_COACH_BUILD === 'true');
        const mentorState = mentorAiService.initialize({ testingPhase, coachBuild });
        
        configService.initialize({
          testingPhase: testingPhase,
          testingWebSocket: true,
          client: import.meta.env.VITE_MENTORAI_CLIENT || mentorState.client,
          assistantID: mentorState.assistantID || resolveAssistantId(testingPhase),
          promptReference: resolvePromptReference(testingPhase) || mentorState.promptReference,
        });

        // En desarrollo local, siempre usar email hardcodeado para pruebas
        let userEmail = '';
        if (testingPhase) {
          // console.log('[App] Modo desarrollo: usando email hardcodeado lauraai@lidarit.com');
          configService.setUserInfo('lauraai@lidarit.com', 'Laura Valero', undefined, 'Laura', 'Valero');
          userEmail = 'lauraai@lidarit.com';
        } else {
          // En producción, intentar obtener email desde token de MentorAI App
          const authPayload = await authService.processAuth();
          
          if (authPayload && authPayload.email) {
            // Usar email obtenido del token de MentorAI App
            // console.log('[App] Email obtenido desde MentorAI App:', authPayload.email);
            configService.setUserInfo(
              authPayload.email,
              authPayload.full_name || 'Usuario',
              authPayload.user_id ? parseInt(authPayload.user_id) : undefined,
              undefined,
              undefined
            );
            userEmail = authPayload.email;
          } else {
            // No hay autenticación válida - bloquear acceso
            // console.warn('[App] ⛔ No se encontró autenticación válida. Bloqueando acceso.');
            setIsAuthBlocked(true);
            markAsInitialized();
            return;
          }
        }

        // Obtener resumen de la última interacción del estudiante (no bloquea la UI)
        if (userEmail) {
          configService.fetchPreviousSummary(userEmail).catch((error) => {
            // console.warn('[App] ⚠️ No se pudo obtener el resumen previo:', error);
          });
        }

        // Activar utilidades de prueba (solo en desarrollo)
        if (testingPhase) {
          enableTestAcademicMode();
          
          // Función rápida para activar modo académico de prueba
          if (typeof window !== 'undefined') {
            (window as any).quickTestAcademic = () => {
              setAcademicLevel({ level: 'A1.1', unit: 0, lesson: 1 });
              setShowAcademicMode(true);
            };
          }
        }

        // Las preferencias no se guardan en localStorage por ahora
        // El menú siempre aparecerá al inicio

        // Inicializar tiempo de interacción
        try {
          interactionTimeService.initialize();
        } catch (error) {
          // console.warn('[App] ⚠️ Error al inicializar interactionTimeService:', error);
        }

        // Configurar callbacks del WebSocket
        // Inicializar handler de cierre del navegador
        try {
          browserCloseHandler.initialize();
        } catch (error) {
          // console.warn('[App] ⚠️ Error al inicializar browserCloseHandler:', error);
        }

        // IMPORTANTE: Marcar como inicializado ANTES de las llamadas asíncronas
        // Las llamadas asíncronas se ejecutarán en segundo plano y no deben bloquear el renderizado
        markAsInitialized();
        
      } catch (error) {
        // console.error('[App] ❌ Error en bloque de inicialización:', error);
        markAsInitialized();
      }
      
      // Las llamadas asíncronas continúan en segundo plano pero no bloquean la UI
      })
      .catch((error) => {
        // Si hay un error en la inicialización, loguearlo pero continuar de todas formas
        // console.error('[App] ❌ Error en inicialización:', error);
        markAsInitialized();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  const handleRecordingComplete = async (audioBlob: Blob) => {
    try {
      // Enviar audio a WebSocket
      await sendAudioMessage(audioBlob);

    } catch (error) {
      // console.error('Error al procesar grabación:', error);
    }
  };

  if (!isInitialized) {
    return (
      <div className="loading-screen">
        <p>Cargando...</p>
      </div>
    );
  }

  if (isAuthBlocked) {
    return (
      <div className="auth-blocked-screen">
        <div className="auth-blocked-content">
          <div className="auth-blocked-icon">🔒</div>
          <h2>Acceso restringido</h2>
          <p>Para usar esta aplicación debes iniciar sesión primero.</p>
          <p className="auth-blocked-hint">Por favor, ingresa desde tu plataforma de acceso.</p>
        </div>
      </div>
    );
  }

  const normalizeMetodoActividad = (raw: string): string => {
    if (!raw) {
      return '';
    }
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  };

  const parseActivitiesResponse = (response: any): ActivityItem[] => {
    const items = Array.isArray(response?.Items) ? response.Items : [];
    return items
      .map((item: any) => ({
        id: item.id?.S || '',
        nombreActividad: item.nombreActividad?.S || 'Actividad sin nombre',
        nivel: item.nivel?.S || '',
        objetivo: item.objetivo?.S || '',
        metodoActividad: item.metodoActividad?.S || '',
        vocabulario: item.vocabulario?.S || '',
        grupo: item.grupo?.S || 'General',
      }))
      .filter((item: ActivityItem) => item.id);
  };

  const loadActivities = async () => {
    setIsActivitiesLoading(true);
    setActivitiesError(null);
    try {
      // Paso 1: Obtener email del usuario desde configService
      const config = configService.getConfig();
      const userEmail = config.email;

      if (!userEmail) {
        throw new Error('No se encontró email del usuario. No se pueden obtener actividades filtradas.');
      }

      // console.log(`[App] Obteniendo grupo para usuario: ${userEmail}`);

      // Paso 2: Obtener grupo del estudiante desde Google Sheets
      const grupo = await googleSheetsService.getStudentGroup(userEmail);
      // console.log(`[App] Grupo obtenido para ${userEmail}: "${grupo}"`);

      // Paso 3: Obtener actividades filtradas por grupo
      const response = await dynamoService.getActivitiesByGroup(grupo);
      const parsed = parseActivitiesResponse(response);
      setActivities(parsed);

      // Paso 4: Cargar actividades completadas desde DynamoDB
      try {
        const completedIds = await dynamoService.getCompletedActivities(userEmail);
        if (completedIds.length > 0) {
          setCompletedActivityIds(new Set(completedIds));
        }
      } catch { /* ignore - no bloquear si falla */ }
      
      // console.log(`[App] ${parsed.length} actividades encontradas para el grupo "${grupo}"`);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'No se pudieron cargar las actividades';
      setActivitiesError(message);
      setActivities([]);
      // console.error('[App] Error al cargar actividades:', error);
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const handleReturnToMenu = () => {
    if (isActivityMenuOpen) {
      setIsActivityMenuOpen(false);
      return;
    }
    setIsChatVisible(false);
    setIsActivityMenuOpen(true);
    loadActivities().catch(() => undefined);
  };

  const handleActivitySelect = async (activity: ActivityItem) => {
    setIsActivityMenuOpen(false);
    setActivitiesError(null);
    setCurrentActivityId(activity.id);
    const metodoActividad = normalizeMetodoActividad(activity.metodoActividad);
    try {
      await webSocketService.startActivitySession({
        nombreActividad: activity.nombreActividad,
        nivel: activity.nivel,
        objetivo: activity.objetivo,
        metodoActividad,
        vocabulario: activity.vocabulario,
        grupo: activity.grupo,
      });
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'No se pudo iniciar la actividad';
      setActivitiesError(message);
      setIsActivityMenuOpen(true);
      setCurrentActivityId(null);
    }
  };

  const handleActivityComplete = async (activity: ActivityItem) => {
    const updated = new Set(completedActivityIds).add(activity.id);
    setCompletedActivityIds(updated);
    setIsActivityMenuOpen(false);
    setCurrentActivityId(null);

    // Persistir en DynamoDB
    try {
      const config = configService.getConfig();
      if (config.email) {
        await dynamoService.saveCompletedActivity(
          config.email,
          activity.id,
          activity.nombreActividad,
          activity.grupo
        );
      }
    } catch { /* ignore - el estado local ya se actualizó */ }

    try {
      await webSocketService.endActivitySession();
    } catch (error) {
      // Continuar aunque haya error
    }
  };

  const handleConfigurationComplete = async (userConfig: UserConfiguration) => {
    
    // Usar userConfiguration si está disponible, sino usar previousConfigurationRef
    const configToCompare = userConfiguration || previousConfigurationRef.current;
    
    // Comparar con la configuración anterior para determinar si es la misma
    const isSameConfiguration = configToCompare && 
      configToCompare.mode === userConfig.mode &&
      configToCompare.rasgo === userConfig.rasgo &&
      configToCompare.acento === userConfig.acento &&
      configToCompare.character === userConfig.character;
    
    const hasPreviousConfig = !!configToCompare;
    
    // Si la configuración es diferente Y hay una configuración previa, guardar datos anteriores y limpiar
    if (!isSameConfiguration && hasPreviousConfig) {
      // Obtener mensajes actuales antes de limpiar
      const currentMessages = webSocketService.getChatHistory();
      
      // Si hay mensajes, enviarlos al lambda antes de limpiar
      if (currentMessages.length > 0) {
        const finalMessages = currentMessages.map((m) => ({
          role: m.role,
          content: m.content,
          afterInvalidMessage: m.afterInvalidMessage,
        }));
        
        try {
          await conversationSaverService.guardarData(finalMessages);
        } catch (error) {
          // console.error('[App] ❌ Error al guardar datos anteriores:', error);
        }
      }
      
      // Limpiar todas las colas de audio y mensajes pendientes
      webSocketService.clearAllQueues();
      
      // Limpiar historial de chat y resetear estado
      webSocketService.clearChatHistory();
      conversationSaverService.resetConversationState();
      
      // Desconectar WebSocket para reiniciar con nueva configuración
      webSocketService.disconnect();
      setShouldConnect(false);
      
      // Esperar un poco para que la desconexión se complete antes de continuar
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Actualizar promptReference según el modo seleccionado
    configService.setPromptReference(userConfig.promptReference);
    
    // Guardar modo y acento en configService para que webSocketService pueda usarlos
    configService.setPracticeModeAndAccent(userConfig.mode, userConfig.acento);

    configService.setVoiceSelection(
      userConfig.voice,
      userConfig.language,
      userConfig.character || ''
    );
    
    // Establecer configuración en conversationSaverService
    conversationSaverService.setCurrentConfiguration(
      userConfig.mode,
      userConfig.rasgo,
      userConfig.acento,
      userConfig.character
    );
    
    // Inicializar rastreo del chat (inicialmente cerrado)
    conversationSaverService.setChatVisibility(false);
    
    // Guardar configuración completa
    setUserConfiguration(userConfig);
    setInitialConfigComplete(true);
    
    // El banner de feedback se mostrará automáticamente cuando haya mensajes
    // (se verifica dinámicamente en el render)
    
    // Limpiar el ref de configuración anterior DESPUÉS de guardar la nueva configuración
    // Esto permite que la próxima vez que se presione "Home" y luego "Aceptar", se pueda comparar correctamente
    previousConfigurationRef.current = null;
    
    // Activar conexión WebSocket después de completar configuración
    // Siempre reconectar cuando la configuración cambia o es la primera vez
    if (!isSameConfiguration) {
      if (hasPreviousConfig) {
        // Configuración diferente: ya se desconectó arriba, ahora reconectar
        setTimeout(() => {
          setShouldConnect(true);
        }, 200);
      } else {
        // Primera vez: conectar directamente
        setShouldConnect(true);
      }
    } else {
      // Misma configuración: solo conectar si no está conectado
      if (!shouldConnect) {
        setShouldConnect(true);
      }
    }
  };

  const handleSelectionConfirm = async (selection: SelectionConfig) => {
    const config = configService.getConfig();
    const mentorState = mentorAiService.getState();
    const promptReference = resolvePromptReference(!!config.testingPhase) || mentorState.promptReference;

    const userConfig: UserConfiguration = {
      mode: 'conversacional',
      promptReference,
      rasgo: 'Neutral',
      acento: selection.accent,
      character: selection.character,
      voice: selection.voice,
      language: selection.language,
    };

    await handleConfigurationComplete(userConfig);
  };

  const handleToggleAudioPause = () => {
    if (isAudioPaused) {
      webSocketService.resumeAudioPlayback();
      setIsAudioPaused(false);
    } else {
      webSocketService.pauseAudioPlayback();
      setIsAudioPaused(true);
    }
  };

  const handleUploadActivity = async () => {
    if (isUploadingActivity) {
      return;
    }
    // Ocultar el chat cuando se presiona el botón de actividades
    setIsChatVisible(false);
    try {
      setIsUploadingActivity(true);
      setIsUploadSuccess(false);
      setUploadErrorMessage(null);

      const chatHistory = webSocketService.getChatHistory();
      const sourceMessages = chatHistory.length > 0 ? chatHistory : messages;

      const analysis = await activityAnalysisService.analyzeConversation(sourceMessages);
      const metodoActividadJson = JSON.stringify(analysis.metodoActividad);
      const activityId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `act-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      await dynamoService.putActividadInDynamoDB(
        activityId,
        analysis.objetivo,
        analysis.nivel,
        metodoActividadJson,
        analysis.vocabulario,
        analysis.nombreActividad,
        analysis.grupo
      );

      setIsUploadSuccess(true);
      setTimeout(() => setIsUploadSuccess(false), 2000);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Error desconocido al subir actividad';
      setUploadErrorMessage(message);
      // console.error('[App] Error al subir actividad:', error);
    } finally {
      setIsUploadingActivity(false);
    }
  };

  return (
    <LayoutManager>
      <div className={`app-container ${isAcademicProgressVisible ? 'academic-sidebar-visible' : ''}`}>
        {(() => {return null;
        })()}
        {!initialConfigComplete && (
          <SelectionMenu onConfirm={handleSelectionConfirm} />
        )}

        {initialConfigComplete && userConfiguration && (
          <Avatar3D character={userConfiguration.character} isChatVisible={isChatVisible} />
        )}

        {isActivityMenuOpen && (
          <ActivitySelectionMenu
            activities={activities}
            isLoading={isActivitiesLoading}
            errorMessage={activitiesError}
            onSelect={handleActivitySelect}
            onComplete={handleActivityComplete}
            onClose={() => setIsActivityMenuOpen(false)}
            currentActivityId={currentActivityId}
            completedActivityIds={completedActivityIds}
          />
        )}

        {(() => {return null;
        })()}
        {(() => {const shouldRender = initialConfigComplete && userConfiguration;
          const config = configService.getConfig();if (!shouldRender) {return null;
          }return (
            <>
            {/* Modo Académico - Mantener montado para preservar estado */}
            {showAcademicMode && academicLevel && config.email && (
              <div className={`academic-mode-container ${isAcademicProgressVisible ? 'visible' : 'hidden'}`}>
                <AcademicMode 
                  userEmail={config.email} 
                  showProgressMap={true}
                  onClose={() => {
                    // Solo minimizar el panel, no resetear estado
                    setIsAcademicProgressVisible(false);
                    // Verificar si hay una lección en proceso de forma asíncrona sin bloquear
                    checkLessonInProgress().catch(console.error);
                  }}
                  onActivityStart={() => setIsLessonInProgress(true)}
                  onActivityEnd={() => setIsLessonInProgress(false)}
                />
              </div>
            )}
            </>
          );
        })()}
        
        <div className={`ui-controls ${isFeedbackBannerExpanded ? 'hidden' : ''}`}>
          <div className="ui-controls-left">
            <Chat 
              messages={messages} 
              onSendMessage={sendTextMessage}
              inline={true}
              isVisibleControlled={isChatVisible}
              onVisibilityChange={handleChatVisibilityChange}
              mode={userConfiguration?.mode}
              enableCopyPaste={true}
              enableAudioReplay={true}
            />
            <PauseButton isPaused={isAudioPaused} onToggle={handleToggleAudioPause} />
          </div>
          <div className="ui-controls-center">
            <MicrophoneButton onRecordingComplete={handleRecordingComplete} />
          </div>
          <div className="ui-controls-right">
            <HomeButton onClick={handleReturnToMenu} />
            <UploadActivityButton
              onClick={handleUploadActivity}
              isUploading={isUploadingActivity}
              isSuccess={isUploadSuccess}
              disabled={isCoachFromURL() === true}
            />
            {!isAcademicProgressVisible && (
              <>
                {userConfiguration?.mode === 'estudio' && showAcademicMode && academicLevel && configService.getConfig().email && (
                  <AcademicProgressButton 
                    onClick={() => setIsAcademicProgressVisible(true)}
                    isInProgress={isLessonInProgress}
                  />
                )}
                
              </>
            )}
          </div>
        </div>
        {uploadErrorMessage && (
          <div className="upload-activity-toast" role="alert">
            {uploadErrorMessage}
          </div>
        )}

        {isConnecting && shouldConnect && (
          <div className="connection-status">
            <div className="connection-spinner"></div>
            <p>Conectando con el servidor...</p>
          </div>
        )}
      </div>

      {/* Banner de feedback - solo mostrar si hay conversación guardable */}
      {showFeedbackBanner && initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user') && (
        <FeedbackBanner
          isVisible={true}
          onSubmit={async (rating, feedback) => {
            await feedbackService.sendFeedback(rating, feedback);
            
            // Enviar la conversación al servidor AHORA con el feedback incluido
            try {
              const chatHistory = webSocketService.getChatHistory();
              const sourceMessages = chatHistory.length > 0 ? chatHistory : messages;
              if (sourceMessages.length > 0) {
                const finalMessages = sourceMessages.map((m) => ({
                  role: m.role,
                  content: m.content,
                  afterInvalidMessage: m.afterInvalidMessage,
                }));
                await conversationSaverService.guardarData(finalMessages);
                // console.log('[App] Conversación re-enviada con feedback incluido');
              }
            } catch (error) {
              // console.error('[App] Error al re-enviar conversación con feedback:', error);
            }
            
            // Asegurar que los botones reaparezcan
            setIsFeedbackBannerExpanded(false);
            setShowFeedbackBanner(false);
          }}
          onExpandChange={(isExpanded) => {
            setIsFeedbackBannerExpanded(isExpanded);
            // Cerrar el chat cuando el banner se expande
            if (isExpanded && isChatVisible) {
              handleChatVisibilityChange(false);
            }
          }}
        />
      )}
    </LayoutManager>
  );
}

export default App;
